import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronLeftIcon, CloseIcon } from '@chakra-ui/icons';
import { Input, InputGroup, InputLeftElement, InputRightElement, Spinner, Switch, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useSetAtom } from 'jotai';
import { motion } from 'motion/react';
import { InitSearchResponseData } from '@/../../server/types/apiTypes';
import { SearchMode } from '@/../../shared/types/searchTypes';
import { useGate } from '@/hooks/useGate';
import { initSearchAtom } from '@/store/searchAtom';
import { sizing } from '@/styles/sizing';

export const Route = createFileRoute('/_privateRoutes/ask-your-friends/')({
  component: RouteComponent,
});

interface createAYFParams {
  query: string;
  mode: SearchMode;
}

interface initSearchParams {
  query: string;
  mode: SearchMode;
}

const createAYF = async ({ query, mode }: createAYFParams) => {
  const response = await axios.post('/api/askFriends', { query, mode });
  return response.data;
};

const initSearch = async ({ query, mode }: initSearchParams): Promise<InitSearchResponseData> => {
  const response = await axios.post('/api/search/initSearch', { query, mode });
  return response.data.data;
};

function RouteComponent() {
  const [question, setQuestion] = useState('');
  const [isIncognitoMode, setIsIncognitoMode] = useState(false);
  const navigate = useNavigate({ from: '/ask-your-friends' });
  const searchBarRef = useRef<HTMLInputElement>(null);
  const checkGateWebSearch = useGate('web_search');
  const setInitSearchState = useSetAtom(initSearchAtom);

  const { mutateAsync: createAYFMutation, isPending: isCreateAYFPending } = useMutation({
    mutationFn: createAYF,
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: initSearch,
  });

  useEffect(() => {
    if (searchBarRef.current) {
      searchBarRef.current.focus();
    }
  }, []);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!question) {
      return;
    }

    if (e.key === 'Enter') {
      if (!checkGateWebSearch()) {
        const { multiAskId } = await createAYFMutation({
          query: question,
          mode: isIncognitoMode ? SearchMode.INCOGNITO : SearchMode.STANDARD,
        });
        navigate({ to: '/ask-your-friends/$multiAskId', params: { multiAskId } });
      } else {
        const { multiAskId, receivers } = await mutateAsync({
          query: question,
          mode: isIncognitoMode ? SearchMode.INCOGNITO : SearchMode.STANDARD,
        });
        setInitSearchState({
          query: question,
          mode: isIncognitoMode ? SearchMode.INCOGNITO : SearchMode.STANDARD,
          multiAskId,
          receivers,
        });
        navigate({ to: '/ask-your-friends/$multiAskId', params: { multiAskId } });
      }
    }
  };

  const showClearButton = useMemo(() => question.length > 0, [question]);
  return (
    <div className="mx-auto" style={{ maxWidth: `${sizing.maxAppWidthPx}px` }}>
      <div className="px-4 py-4 w-full fixed top-0 bg-[#1c1c1c] z-10" style={{ maxWidth: `${sizing.maxAppWidthPx}px` }}>
        <motion.div className="w-full" layoutId="main-search-input" transition={{ duration: 0.25 }}>
          <InputGroup>
            <InputLeftElement
              pointerEvents="fill"
              color="gray.300"
              fontSize="1.5em"
              height="100%"
              onClick={() => navigate({ to: '/' })}
            >
              <ChevronLeftIcon className="cursor-pointer" />
            </InputLeftElement>
            <Input
              ref={searchBarRef}
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              bg="white"
              color="black"
              borderRadius="full"
              py={4}
              height="56px"
              border="none"
              onKeyDown={handleKeyDown}
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              enterKeyHint="search"
            />
            <InputRightElement pointerEvents="fill" color="gray.300" fontSize="0.7em" height="100%">
              {showClearButton && (
                <CloseIcon
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setQuestion('');
                  }}
                />
              )}
            </InputRightElement>
          </InputGroup>
        </motion.div>
        <div className="flex items-center gap-2 mt-3">
          <Switch
            size="lg"
            sx={{
              '& .chakra-switch__track[data-checked]': {
                backgroundColor: '#ff0050',
              },
            }}
            onChange={(e) => {
              setIsIncognitoMode(e.target.checked);
            }}
            isChecked={isIncognitoMode}
          />
          <Text color="white" fontSize="xs">
            Incognito Mode
          </Text>
        </div>
      </div>
      <div className="flex justify-center items-center h-screen">
        {(isCreateAYFPending || isPending) && (
          <Spinner thickness="4px" speed="0.75s" emptyColor="#1c1c1c" color="#FF0050" size="md" />
        )}
      </div>
    </div>
  );
}
